// Карта, боссы и бой — tech/03 §6, протокол — tech/04. Прототип: D1, одиночный режим, E03-T02, E04-T03.
import { z } from 'zod';
import { BossId, DistrictId, EpochMs, IsoTime, NextCursor, PageQuery, Uuid7, strict } from '../common.js';
import { BattleMode, BattleResult, BattleStart, BattleState, MoveId, TurnResult } from '../combat.js';
import { endpoint } from '../endpoint.js';

/** Звезда сезона (03 §6.1). Не прототип, но поле есть в ответе `/districts`. */
export const SeasonStarView = strict({
  bossId: BossId,
  variant: z.string().optional(),
  seasonId: z.string().regex(/^SEA-[0-9]{2,3}$/),
  status: z.enum(['announce', 'active']),
  minLevel: z.int().positive(),
  lStar: z.int().positive(),
  modes: z.array(BattleMode),
  opensAt: IsoTime,
  closesAt: IsoTime,
  coopCallUntil: IsoTime,
  unlocked: z.boolean(),
  coopInstance: z.unknown().optional(),
  soloWinsSeason: z.int().nonnegative(),
});

export const CoopInstanceBrief = strict({ iid: Uuid7, hp: z.int().nonnegative(), hpMax: z.int().positive(), endsAt: IsoTime });

export const DistrictsResponse = strict({
  districts: z.array(strict({
    districtId: DistrictId,
    open: z.boolean(),
    /**
     * Чего не хватает закрытому району (ГД 06 §2: уровень и одиночная победа над боссом предыдущего района) —
     * клиент строит подсказку «Сначала разберись с Палычем» (E04-T03). Нет у открытого района. Решение Р-30.
     */
    lock: strict({ level: z.int().positive().optional(), bossId: BossId.optional() }).optional(),
    bosses: z.array(strict({
      bossId: BossId,
      unlocked: z.boolean(),
      /** Подписи точки на карте (ux/04 §1.2): «закрыт уровнем N», «×N побед», «доступна Зачистка». */
      minLevel: z.int().positive().optional(),
      winsSolo: z.int().nonnegative().optional(),
      sweep: z.boolean().optional(),
      variant: z.string().optional(),
      coopInstance: CoopInstanceBrief.optional(),
      /**
       * «Рекомендуемая сила» (Р-104): урон эталонного захода Fd (ГД 05 §8.3) по броне босса — моего снимка и эталона
       * уровня босса. Клиент показывает долю `mine / recommended` («Сила 62% от рекомендуемой»).
       */
      power: strict({ mine: z.int().nonnegative(), recommended: z.int().positive() }).optional(),
      /** Закончившийся общий инстанс, итог которого игрок (участник) ещё не открыл — подарок на точке (ux/04 §1; Р-89). */
      coopResult: strict({ iid: Uuid7, outcome: z.enum(['defeated', 'expired']), unseen: z.literal(true) }).optional(),
    })),
  })),
  seasonStar: SeasonStarView.optional(),
});

export const getDistricts = endpoint({
  id: 'map.districts', method: 'GET', path: '/districts', auth: true, signed: false, idempotency: 'read', rate: 'read',
  response: DistrictsResponse, status: 200, errors: [], spec: 'tech/03 §6; ГД 06 §2', task: 'E04-T03',
});

const BattleIdParams = strict({ battleId: Uuid7 });

export const StartBattleBody = strict({ bossId: BossId, mode: BattleMode, fullTank: z.boolean().optional() });

export const startBattle = endpoint({
  id: 'battle.start', method: 'POST', path: '/battles', auth: true, signed: true, idempotency: 'key-required', rate: 'write',
  body: StartBattleBody, response: BattleStart, status: 201,
  errors: ['BATTLE_ALREADY_ACTIVE', 'BOSS_DEFEATED', 'GONE', 'NOT_ENOUGH_ENERGY', 'NOT_ENOUGH_DIESEL', 'BOSS_LOCKED',
    'MODE_NOT_ALLOWED', 'NOT_IN_COOP', 'LEVEL_TOO_LOW', 'REQUIREMENT_NOT_MET'],
  spec: 'tech/03 §6; tech/04 §3', task: 'E03-T02',
});

export const getActiveBattle = endpoint({
  id: 'battle.active', method: 'GET', path: '/battles/active', auth: true, signed: false, idempotency: 'read', rate: 'read',
  response: BattleState, status: 200, allowNoContent: true, errors: [], spec: 'tech/03 §6; tech/04 §10', task: 'E03-T02',
});

/**
 * Ход (04 §4.1). Только намерение: никаких полей результата — `.strict()` отвергает `damage` и т. п.
 * (критерий выхода этапа П п. 2: «поддельный запрос с полем damage → 400»).
 */
export const TurnBody = strict({
  turnNo: z.int().min(1).max(64),
  moveId: MoveId,
  auto: z.boolean(),
  clientTs: EpochMs,
});

export const battleTurn = endpoint({
  id: 'battle.turn', method: 'POST', path: '/battles/{battleId}/turns', auth: true, signed: true, idempotency: 'natural', rate: 'turn',
  params: BattleIdParams, body: TurnBody, response: TurnResult, status: 200,
  errors: ['NOT_FOUND', 'STALE_TURN', 'BATTLE_OVER', 'COOLDOWN', 'NOT_ENOUGH_DIESEL'],
  spec: 'tech/03 §6; tech/04 §4', task: 'E03-T02',
});

export const battleRetreat = endpoint({
  id: 'battle.retreat', method: 'POST', path: '/battles/{battleId}/retreat', auth: true, signed: true, idempotency: 'state', rate: 'write',
  params: BattleIdParams, body: strict({}), response: BattleResult, status: 200,
  errors: ['NOT_FOUND', 'BATTLE_OVER'], spec: 'tech/03 §6; tech/04 §6 (X-20)', task: 'E03-T02',
});

export const getBattle = endpoint({
  id: 'battle.get', method: 'GET', path: '/battles/{battleId}', auth: true, signed: false, idempotency: 'read', rate: 'read',
  params: BattleIdParams, response: BattleResult, status: 200, errors: ['NOT_FOUND'], spec: 'tech/03 §6', task: 'E03-T02',
});

/** История боёв: форма элемента в 03 §6 не задана (Р-02). */
export const getBattles = endpoint({
  id: 'battle.history', method: 'GET', path: '/battles', auth: true, signed: false, idempotency: 'read', rate: 'read',
  query: PageQuery,
  response: strict({
    items: z.array(strict({
      battleId: Uuid7, bossId: BossId, mode: BattleMode, outcome: BattleResult.shape.outcome,
      rounds: z.int().min(0).max(10), damageEffective: z.int().nonnegative(), at: IsoTime,
    })),
    nextCursor: NextCursor,
  }),
  status: 200, errors: [], spec: 'tech/03 §6', task: 'E03-T02',
});
